import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/contexts/AuthContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent } from '@/components/ui/card';
import { Phone, PhoneOff, ArrowRight, ArrowLeft, MapPin, Building2, User, Mail, Clock, CheckCircle, Target, X, Timer, SkipForward, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import VoiceNoteButton from '@/components/VoiceNoteButton';
import CallScriptDialog from '@/components/CallScriptDialog';

const STATUTS = [
  { value: 'appele', label: 'Appelé', color: '#0055FF' },
  { value: 'rdv_obtenu', label: 'RDV obtenu', color: '#10B981' },
  { value: 'en_negociation', label: 'En négociation', color: '#8B5CF6' },
  { value: 'refus', label: 'Refus', color: '#EF4444' },
  { value: 'rappel_demande', label: 'Rappel demandé', color: '#F59E0B' },
  { value: 'injoignable', label: 'Injoignable', color: '#71717A' },
  { value: 'mauvais_numero', label: 'Mauvais n°', color: '#EF4444' },
  { value: 'mail_prospection_envoye', label: 'Mail Prospection envoyé', color: '#0055FF' },
  { value: 'mail_doc_envoye', label: 'Mail Doc envoyé', color: '#8B5CF6' },
  { value: 'converti', label: 'Converti', color: '#059669' },
];

export default function CallModePage() {
  const { user } = useAuth();
  const [prospects, setProspects] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [sessionStats, setSessionStats] = useState({ appels: 0, contacts: 0, rdv: 0, refus: 0, start: Date.now() });
  const [elapsed, setElapsed] = useState(0);
  const [filter, setFilter] = useState('a_appeler');

  const loadProspects = useCallback(async () => {
    setLoading(true);
    const params = { per_page: 200, sort_by: 'score_confiance', sort_order: 'desc' };
    if (filter === 'a_appeler') params.statut_commercial = 'a_appeler';
    else if (filter === 'nouveau') params.statut_commercial = 'nouveau';
    else if (filter === 'rappel') params.statut_commercial = 'rappel_demande';
    else if (filter === 'injoignable') params.statut_commercial = 'injoignable';
    try {
      const { data } = await api.get('/prospects', { params });
      setProspects(data.prospects.filter(p => p.telephone && p.telephone !== '?'));
      setCurrentIndex(0);
    } catch {}
    setLoading(false);
  }, [filter]);

  useEffect(() => { loadProspects(); }, [loadProspects]);

  // Session timer
  useEffect(() => {
    const timer = setInterval(() => setElapsed(Math.floor((Date.now() - sessionStats.start) / 1000)), 1000);
    return () => clearInterval(timer);
  }, [sessionStats.start]);

  const prospect = prospects[currentIndex];
  const formatTime = (s) => `${Math.floor(s / 3600).toString().padStart(2, '0')}:${Math.floor((s % 3600) / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;

  const handleCallResult = async (resultat) => {
    if (!prospect) return;
    try {
      await api.post(`/prospects/${prospect.id}/call-result`, { resultat, commentaire: comment });
      setSessionStats(prev => ({
        ...prev,
        appels: prev.appels + 1,
        contacts: prev.contacts + (['appele', 'rdv_obtenu', 'en_negociation', 'refus', 'converti'].includes(resultat) ? 1 : 0),
        rdv: prev.rdv + (resultat === 'rdv_obtenu' ? 1 : 0),
        refus: prev.refus + (resultat === 'refus' ? 1 : 0),
      }));
      setComment('');
      toast.success(`${resultat === 'rdv_obtenu' ? 'RDV obtenu !' : 'Résultat enregistré'}`);
      // Next prospect
      if (currentIndex < prospects.length - 1) {
        setCurrentIndex(i => i + 1);
      } else {
        toast.info('Fin de la liste !');
        loadProspects();
      }
    } catch { toast.error('Erreur'); }
  };

  const skipProspect = () => {
    if (currentIndex < prospects.length - 1) setCurrentIndex(i => i + 1);
  };

  if (loading) return <div className="flex items-center justify-center h-[70vh] text-[#71717A]">Chargement...</div>;

  return (
    <div className="space-y-4 animate-fade-in" data-testid="call-mode-page">
      {/* Header bar */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>Mode Appel</h1>
          <p className="text-sm text-[#71717A]">{prospects.length} prospects à appeler</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-44 h-9 bg-white" data-testid="call-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="a_appeler">À appeler</SelectItem>
              <SelectItem value="nouveau">Nouveaux</SelectItem>
              <SelectItem value="rappel">Rappels</SelectItem>
              <SelectItem value="injoignable">Injoignables</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Session stats bar */}
      <div className="grid grid-cols-5 gap-3">
        {[
          { label: 'Durée', value: formatTime(elapsed), icon: Timer, color: '#71717A' },
          { label: 'Appels', value: sessionStats.appels, icon: Phone, color: '#0055FF' },
          { label: 'Contacts', value: sessionStats.contacts, icon: CheckCircle, color: '#10B981' },
          { label: 'RDV', value: sessionStats.rdv, icon: Target, color: '#F97316' },
          { label: 'Taux contact', value: sessionStats.appels > 0 ? `${Math.round((sessionStats.contacts / sessionStats.appels) * 100)}%` : '-', icon: Target, color: '#8B5CF6' },
        ].map((s, i) => (
          <Card key={i} className="border border-[#E5E7EB]">
            <CardContent className="p-3 flex items-center gap-2">
              <s.icon className="w-4 h-4" style={{ color: s.color }} />
              <div>
                <p className="text-lg font-bold" style={{ fontFamily: 'var(--font-heading)', color: s.color }}>{s.value}</p>
                <p className="text-[10px] text-[#71717A]">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {!prospect ? (
        <Card className="border border-[#E5E7EB]">
          <CardContent className="p-12 text-center">
            <CheckCircle className="w-12 h-12 text-[#10B981] mx-auto mb-3" />
            <h3 className="text-lg font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>Liste terminée !</h3>
            <p className="text-sm text-[#71717A]">Tous les prospects ont été traités. Changez de filtre ou rechargez.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left: Prospect card */}
          <Card className="lg:col-span-2 border border-[#E5E7EB] shadow-sm">
            <CardContent className="p-6">
              {/* Progress */}
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs text-[#71717A] font-medium">{currentIndex + 1} / {prospects.length}</span>
                <div className="flex gap-1">
                  <CallScriptDialog
                    prospectId={prospect.id}
                    prospectName={prospect.nom_magasin || prospect.enseigne}
                    trigger={
                      <Button variant="outline" size="sm" className="h-7 gap-1 text-xs border-[#F97316] text-[#F97316] hover:bg-[#FFF7ED]" data-testid="callmode-script-ia-btn">
                        <Sparkles className="w-3.5 h-3.5" /> Script IA
                      </Button>
                    }
                  />
                  <VoiceNoteButton prospectId={prospect.id} size="sm" className="h-7 text-xs" />
                  <Button variant="outline" size="sm" disabled={currentIndex === 0} onClick={() => setCurrentIndex(i => i - 1)} className="h-7 w-7 p-0">
                    <ArrowLeft className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={skipProspect} className="h-7 gap-1 text-xs" data-testid="skip-btn">
                    <SkipForward className="w-3.5 h-3.5" /> Passer
                  </Button>
                </div>
              </div>

              {/* Prospect info */}
              <div className="space-y-4">
                <div>
                  <h2 className="text-xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>{prospect.nom_magasin || prospect.enseigne}</h2>
                  <p className="text-sm text-[#71717A]">{prospect.enseigne} {prospect.marque && `- ${prospect.marque}`}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {/* Phone - BIG */}
                  <div className="col-span-2 p-4 bg-[#F9FAFB] rounded-xl border border-[#E5E7EB]">
                    <a href={`tel:${prospect.telephone?.replace(/\s/g, '')}`}
                      className="flex items-center gap-3 text-2xl font-bold font-mono text-[#0055FF] hover:text-[#0040CC] transition-colors"
                      data-testid="call-phone-link">
                      <div className="w-12 h-12 rounded-full bg-[#0055FF] flex items-center justify-center flex-shrink-0">
                        <Phone className="w-5 h-5 text-white" />
                      </div>
                      {prospect.telephone}
                    </a>
                    {prospect.score_confiance > 0 && (
                      <p className="text-xs text-[#71717A] mt-2 ml-15">Score: {prospect.score_confiance}% &bull; Source: {prospect.source}</p>
                    )}
                  </div>

                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-[#71717A] mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium">{prospect.rue}</p>
                      <p className="text-[#71717A]">{prospect.code_postal} {prospect.ville}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2">
                    <User className="w-4 h-4 text-[#8B5CF6] mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-[#8B5CF6]">
                        {prospect.prenom_dirigeant && prospect.nom_dirigeant ? `${prospect.prenom_dirigeant} ${prospect.nom_dirigeant}` : 'Non renseigné'}
                      </p>
                      {prospect.raison_sociale && <p className="text-xs text-[#71717A]">{prospect.raison_sociale}</p>}
                    </div>
                  </div>

                  {prospect.email_professionnel && (
                    <div className="col-span-2 flex items-center gap-2">
                      <Mail className="w-4 h-4 text-[#0055FF]" />
                      <a href={`mailto:${prospect.email_professionnel}`} className="text-sm text-[#0055FF] hover:underline">
                        {prospect.email_professionnel}
                      </a>
                    </div>
                  )}
                </div>

                <Separator />

                {/* Notes */}
                <div>
                  <label className="text-xs font-semibold text-[#71717A] uppercase mb-2 block">Commentaire</label>
                  <Textarea value={comment} onChange={e => setComment(e.target.value)}
                    placeholder="Notes de l'appel..." rows={3} className="bg-white border-[#E5E7EB]"
                    data-testid="call-comment" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Right: Actions */}
          <div className="space-y-3">
            <Card className="border border-[#E5E7EB] shadow-sm">
              <CardContent className="p-4">
                <h3 className="text-xs font-semibold text-[#71717A] uppercase mb-3">Résultat de l'appel</h3>
                <div className="space-y-2">
                  {STATUTS.map(s => (
                    <Button key={s.value} variant="outline" onClick={() => handleCallResult(s.value)}
                      data-testid={`call-action-${s.value}`}
                      className="w-full h-10 justify-start gap-2 text-sm font-medium hover:scale-[1.02] transition-all"
                      style={{ borderColor: `${s.color}30`, color: s.color }}>
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                      {s.label}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Script suggestion */}
            <Card className="border border-[#E5E7EB] shadow-sm bg-[#FFFBEB]">
              <CardContent className="p-4">
                <h3 className="text-xs font-semibold text-[#D97706] uppercase mb-2">Script d'appel</h3>
                <div className="text-xs text-[#92400E] space-y-2">
                  <p>"Bonjour, je suis <strong>{user?.prenom}</strong> de la société <strong>Wasabi Corner</strong>.</p>
                  <p>Je souhaiterais parler {prospect.prenom_dirigeant ? `à <strong>${prospect.prenom_dirigeant} ${prospect.nom_dirigeant}</strong>` : 'au responsable du magasin'}, s'il vous plaît."</p>
                  <p className="text-[#D97706] font-medium mt-2">Objet : [votre pitch]</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
